import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/cliente';
import { useSessao } from '../auth/SessaoContext';
import { TituloDaPagina } from '../shell/TituloDaPagina';
import { INATIVO_APOS_DIAS, presencaDe } from './presencaDaEquipe';
import { desvioDoEmpate, limiteDeHoje, serieAteHoje } from './serieDoComparativo';
import { Avatar } from '../comum/Avatar';
import { primeiroNome } from '../comum/iniciais';
import './equipe.css';
import './modal.css';

/** A grade de horas do mês, como vem da API: uma célula por dia do calendário. */
interface GradeHoras {
  anoMes: string;
  dias: Dia[];
  membros: MembroHoras[];
}

interface Dia {
  /** aaaa-MM-dd. */
  data: string;
  dia: number;
  /** 1 = segunda … 7 = domingo. */
  diaSemana: number;
  hoje: boolean;
}

interface MembroHoras {
  id: number;
  nome: string;
  email: string;
  handle: string;
  cargo: string;
  papel: 'ADMIN' | 'EDITOR';
  cor: string;
  /** URL da foto, ou nulo em quem não subiu nenhuma. */
  avatar: string | null;
  /** ISO-8601 da última vez que a pessoa esteve aqui. Nulo em quem nunca entrou. */
  vistoEm: string | null;
  celulas: { data: string; horas: number; descricao: string | null }[];
}

const MESES_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const MES_ABR = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const nomeMes = (anoMes: string) => {
  const [ano, mes] = anoMes.split('-').map(Number);
  return `${MESES_PT[mes - 1]} ${ano}`;
};

const abrevMes = (anoMes: string) => MES_ABR[Number(anoMes.split('-')[1]) - 1];

/** Sábado e domingo — pintados de outro jeito, e é onde a maioria dos dias fica em branco. */
const fimDeSemana = (d: Dia) => d.diaSemana >= 6;

/**
 * Se o cargo diz, à mão, que a pessoa está inativa. Não é um campo de status; é o texto
 * do cargo lido de propósito, e é por isso que a comparação ignora caixa: "Inativo" e
 * "inativo" contam, "Inativo (licença)" não.
 *
 * <p>Este é o sinal <b>manual</b>. O card fica cinza também pelo sinal <b>automático</b>
 * — `presencaDe(...).inativoPorAusencia`, quando ninguém marcou nada e a pessoa só
 * sumiu por {@link INATIVO_APOS_DIAS} dias ou mais. Os dois se somam no card
 * (`CardMembro`), mas continuam sinais diferentes: o automático não reescreve o cargo,
 * só acrescenta um aviso — pedido do Theo, para não perder de vista o cargo real de
 * quem está só em uma pausa longa.
 */
const ehInativo = (cargo: string) => cargo.trim().toLowerCase() === 'inativo';

const DIA_SEMANA_ABR = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'];

/**
 * O mês em que a contagem começa.
 *
 * <p>Antes disto as horas eram lançadas por semana, num bloco de 7 dias que nem seguia o
 * calendário, e o histórico não é comparável com o diário. A navegação de mês para aqui,
 * e o servidor recusa o que vier antes — a tela é a cortesia, não a tranca.
 */
const PRIMEIRO_MES = '2026-08';

/** 12,5 vira "12,5h" — o mesmo formato do protótipo. */
const fmtH = (v: number) => `${(Math.round(v * 10) / 10).toString().replace('.', ',')}h`;

function mudaMes(anoMes: string, delta: number): string {
  let [ano, mes] = anoMes.split('-').map(Number);
  mes += delta;
  if (mes < 1) {
    mes = 12;
    ano--;
  }
  if (mes > 12) {
    mes = 1;
    ano++;
  }
  return `${ano}-${String(mes).padStart(2, '0')}`;
}

/** O mês corrente, nunca antes do primeiro que conta. */
function mesDeHoje(): string {
  const agora = new Date();
  const mes = `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}`;
  return mes < PRIMEIRO_MES ? PRIMEIRO_MES : mes;
}

const totalDe = (m: MembroHoras) => m.celulas.reduce((soma, c) => soma + Number(c.horas), 0);

/**
 * Equipe e horas — port da página do protótipo: cards com sparkline, modal de
 * lançamento por semana com gráfico, e o comparativo de linhas do time.
 */
export function Equipe() {
  const [anoMes, setAnoMes] = useState(mesDeHoje);
  const [membroAberto, setMembroAberto] = useState<number | null>(null);
  const [ocultos, setOcultos] = useState<Set<number>>(new Set());
  const [foco, setFoco] = useState<number | null>(null);

  const { data, isPending, error } = useQuery<GradeHoras>({
    queryKey: ['horas', anoMes],
    queryFn: () => api(`/api/horas?mes=${anoMes}`),
  });

  if (isPending) return <div className="rm-estado">carregando a equipe…</div>;
  if (error) return <div className="rm-estado">não consegui carregar: {error.message}</div>;

  const totalDoTime = data.membros.reduce((soma, m) => soma + totalDe(m), 0);
  const admins = data.membros.filter((m) => m.papel === 'ADMIN').length;
  const aberto = membroAberto != null ? data.membros.find((m) => m.id === membroAberto) : null;

  // Quem mais trabalhou no mês vem na frente. A régua é a do mês em tela — trocar de
  // mês reordena junto. O sort do JS é estável, então empate (0h × 0h, o caso comum)
  // preserva a ordem da API, e os cards não dançam entre visitas.
  const membros = [...data.membros].sort((a, b) => totalDe(b) - totalDe(a));

  return (
    <div>
      <TituloDaPagina>
        Quem tem acesso ao GG Pilot e quantas horas cada um dedicou. Clique num membro para lançar
        as horas de cada dia.
      </TituloDaPagina>

      <div className="acbar">
        <div className="acn">
          {data.membros.length} membros · {admins} admins ·{' '}
          <b style={{ color: 'var(--gold)' }}>{fmtH(totalDoTime)}</b> no mês
        </div>
        <NavegacaoDeMes anoMes={anoMes} mudar={(d) => setAnoMes((m) => mudaMes(m, d))} />
      </div>

      <div className="grid3">
        {membros.map((membro) => (
          <CardMembro key={membro.id} membro={membro} abrir={() => setMembroAberto(membro.id)} />
        ))}
      </div>

      {/* A mesma ordem dos cards, para a legenda do comparativo contar a mesma história. */}
      <ComparativoDoTime
        grade={{ ...data, membros }}
        ocultos={ocultos}
        foco={foco}
        alternar={(id) =>
          setOcultos((atual) => {
            const novo = new Set(atual);
            if (novo.has(id)) novo.delete(id);
            else novo.add(id);
            return novo;
          })
        }
        focar={setFoco}
      />

      {aberto && (
        <ModalHoras
          membro={aberto}
          grade={data}
          anoMes={anoMes}
          mudarMes={(delta) => setAnoMes((m) => mudaMes(m, delta))}
          fechar={() => setMembroAberto(null)}
        />
      )}
    </div>
  );
}

/**
 * A navegação de mês, com o batente de agosto de 2026.
 *
 * <p>O botão de voltar fica desabilitado no primeiro mês em vez de sumir: um botão que
 * some muda o layout e faz a pessoa procurar o que não está mais lá.
 */
/**
 * O selo de presença do card.
 *
 * <p>Reavalia sozinho de minuto em minuto: quem deixa a tela aberta veria "Online" para
 * sempre, porque a régua é calculada na renderização e nada mais a dispara. Um minuto é
 * mais fino que o degrau mais curto (10 min), então nenhuma faixa é pulada.
 */
function PresencaDoMembro({ vistoEm }: { vistoEm: string | null }) {
  const [, redesenhar] = useState(0);

  useEffect(() => {
    const id = setInterval(() => redesenhar((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const { classe, rotulo, titulo } = presencaDe(vistoEm);
  return (
    <span className={`presenca ${classe}`} title={titulo}>
      <span className="ponto" aria-hidden="true" />
      {rotulo}
    </span>
  );
}

function NavegacaoDeMes({ anoMes, mudar }: { anoMes: string; mudar(delta: number): void }) {
  const noComeco = anoMes <= PRIMEIRO_MES;
  return (
    <div className="mesnav">
      <button
        className="mesbtn"
        onClick={() => mudar(-1)}
        disabled={noComeco}
        title={noComeco ? 'As horas do GG Pilot começam em agosto de 2026' : 'Mês anterior'}
      >
        ‹
      </button>
      <div className="meslbl">{nomeMes(anoMes)}</div>
      <button className="mesbtn" onClick={() => mudar(1)} title="Próximo mês">
        ›
      </button>
    </div>
  );
}

function CardMembro({ membro, abrir }: { membro: MembroHoras; abrir(): void }) {
  const total = totalDe(membro);
  const pico = Math.max(1, ...membro.celulas.map((c) => Number(c.horas)));
  const diasLancados = membro.celulas.filter((c) => Number(c.horas) > 0).length;
  const manual = ehInativo(membro.cargo);
  // Dia-a-dia, não precisa de relógio próprio como `PresencaDoMembro` — a virada só
  // acontece na troca de calendário, e a página já é revisitada com bem mais frequência.
  const automatico = presencaDe(membro.vistoEm).inativoPorAusencia;

  return (
    <div
      className={`mc${manual || automatico ? ' inativo' : ''}`}
      style={{ '--c': membro.cor } as React.CSSProperties}
      onClick={abrir}
    >
      <Avatar className="avatar" nome={membro.nome} cor={membro.cor} url={membro.avatar} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="mn">{membro.nome}</div>
        <div className="mmail">{membro.email}</div>
        <div className="mr">{membro.cargo}</div>
        <div className="acrow">
          <span className={`lvl${membro.papel === 'ADMIN' ? ' adm' : ''}`}>
            {membro.papel === 'ADMIN' ? 'Admin' : 'Editor'}
          </span>
          {/* Só quando o cargo ainda não diz "Inativo" — senão o card repetiria o mesmo
              aviso duas vezes, um escrito à mão e outro calculado. */}
          {automatico && !manual && (
            <span className="lvl warn" title={`Sem nenhum sinal há ${INATIVO_APOS_DIAS} dias ou mais`}>
              Inativo
            </span>
          )}
          {/* Substituiu o "● Ativo", que era texto fixo: dizia "Ativo" para todo mundo,
              inclusive para quem nunca tinha entrado. */}
          <PresencaDoMembro vistoEm={membro.vistoEm} />
        </div>
        {/* Uma barra por dia do mês: o desenho do mês inteiro, não de cinco blocos. */}
        <div className="hbar dias">
          {membro.celulas.map((celula) => {
            const h = Number(celula.horas);
            const altura = h ? Math.max(6, Math.round((h / pico) * 100)) : 6;
            const [, , dia] = celula.data.split('-');
            return (
              <div
                key={celula.data}
                className={`b${h ? '' : ' zero'}`}
                style={{ height: `${altura}%` }}
                title={`Dia ${Number(dia)}: ${fmtH(h)}`}
              />
            );
          })}
        </div>
        <div className="htot">
          <b>{fmtH(total)}</b>
          <span>
            em {diasLancados} {diasLancados === 1 ? 'dia' : 'dias'}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Modal de lançamento: gráfico de barras + uma linha de inputs por semana. */
function ModalHoras({
  membro,
  grade,
  anoMes,
  mudarMes,
  fechar,
}: {
  membro: MembroHoras;
  grade: GradeHoras;
  anoMes: string;
  mudarMes(delta: number): void;
  fechar(): void;
}) {
  const { usuario } = useSessao();
  const fila = useQueryClient();
  const podeEditar = usuario?.papel === 'ADMIN' || usuario?.id === membro.id;
  const debounces = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  // Rascunho local dos inputs: grava com debounce, célula a célula.
  const [rascunho, setRascunho] = useState(() =>
    membro.celulas.map((c) => ({ horas: String(c.horas || ''), descricao: c.descricao ?? '' }))
  );

  const lancar = useMutation({
    mutationFn: ({ data, horas, descricao }: { data: string; horas: number; descricao: string }) =>
      api(`/api/horas/${membro.id}/${data}`, {
        method: 'PUT',
        body: JSON.stringify({ horas, descricao: descricao || null }),
      }),
    onSuccess: () => void fila.invalidateQueries({ queryKey: ['horas', anoMes] }),
  });

  const limpar = useMutation({
    mutationFn: () => api(`/api/horas/${membro.id}/mes/${anoMes}`, { method: 'DELETE' }),
    onSuccess: () => {
      void fila.invalidateQueries({ queryKey: ['horas', anoMes] });
      setRascunho(grade.dias.map(() => ({ horas: '', descricao: '' })));
    },
  });

  function editar(indice: number, campo: 'horas' | 'descricao', valor: string) {
    setRascunho((atual) => {
      const novo = [...atual];
      novo[indice] = { ...novo[indice], [campo]: valor };
      return novo;
    });
    clearTimeout(debounces.current.get(indice));
    debounces.current.set(
      indice,
      setTimeout(() => {
        setRascunho((atual) => {
          const celula = atual[indice];
          lancar.mutate({
            data: grade.dias[indice].data,
            horas: parseFloat(celula.horas.replace(',', '.')) || 0,
            descricao: celula.descricao.trim(),
          });
          return atual;
        });
      }, 600)
    );
  }

  const valores = rascunho.map((r) => parseFloat(r.horas.replace(',', '.')) || 0);
  const total = valores.reduce((a, b) => a + b, 0);
  const diasAtivos = valores.filter((v) => v > 0).length;
  const indicePico = valores.reduce((melhor, v, i) => (v > valores[melhor] ? i : melhor), 0);

  return (
    <div className="backdrop" onClick={(e) => e.target === e.currentTarget && fechar()}>
      {/*
        660 → 1080. A largura antiga vinha de quando o modal só tinha a tabela; com o
        gráfico dentro, ela espremia as duas coisas que mais precisam de espaço — a
        descrição do dia, que é texto livre, e o mês inteiro no eixo do gráfico.
      */}
      <div className="modal hm" role="dialog" aria-modal="true">
        <div className="mhead">
          <div className="mtop">
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
              <Avatar
                className="avatar"
                nome={membro.nome}
                cor={membro.cor}
                url={membro.avatar}
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 11,
                  flex: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--fonte-titulo)',
                  fontWeight: 700,
                  fontSize: 19,
                  color: 'var(--void)',
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--fonte-titulo)', fontSize: 22, fontWeight: 600 }}>
                  {membro.nome}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--meta)', marginTop: 2 }}>
                  {membro.email} · {membro.cargo}
                </div>
              </div>
            </div>
            <button className="mclose" onClick={fechar}>
              ×
            </button>
          </div>

          <div className="hm-resumo">
            <div className="rz">
              <b>{fmtH(total)}</b>
              <span>Total no mês</span>
            </div>
            <div className="rz">
              <b>{fmtH(diasAtivos ? total / diasAtivos : 0)}</b>
              <span>Média/dia trabalhado</span>
            </div>
            <div className="rz">
              <b>{diasAtivos}</b>
              <span>Dias lançados</span>
            </div>
            <div className="rz">
              <b>{valores[indicePico] > 0 ? `dia ${grade.dias[indicePico].dia}` : '—'}</b>
              <span>Dia de pico</span>
            </div>
            <NavegacaoDeMes anoMes={anoMes} mudar={mudarMes} />
          </div>
        </div>

        <div className="mbody">
          <GraficoBarras valores={valores} dias={grade.dias} cor={membro.cor} />

          {/*
            Um mês inteiro são 31 linhas, então a tabela rola dentro do modal em vez de
            esticá-lo. Fim de semana vem apagado e hoje vem marcado: sem isso, achar a
            linha certa numa lista de 31 números iguais é contar com o dedo.
          */}
          <div className="htab-rolo">
            <table className="htab">
              <thead>
                <tr>
                  <th style={{ width: 108 }}>Dia</th>
                  <th style={{ width: 90 }}>Horas</th>
                  <th>Em que trabalhou</th>
                </tr>
              </thead>
              <tbody>
                {grade.dias.map((dia, i) => (
                  <tr
                    key={dia.data}
                    className={`${fimDeSemana(dia) ? 'fds' : ''}${dia.hoje ? ' hoje' : ''}`}
                  >
                    <td className="sem">
                      {dia.dia} {abrevMes(anoMes)}
                      <i>
                        {DIA_SEMANA_ABR[dia.diaSemana - 1]}
                        {dia.hoje && ' · hoje'}
                      </i>
                    </td>
                    <td>
                      <input
                        className="h"
                        type="number"
                        min={0}
                        max={24}
                        step={0.5}
                        value={rascunho[i]?.horas ?? ''}
                        placeholder="0"
                        disabled={!podeEditar}
                        onChange={(e) => editar(i, 'horas', e.target.value)}
                      />
                    </td>
                    <td>
                      <CampoQueCresce
                        valor={rascunho[i]?.descricao ?? ''}
                        placeholder="no que trabalhou nesse dia"
                        desabilitado={!podeEditar}
                        aoMudar={(texto) => editar(i, 'descricao', texto)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="hm-foot">
            {!podeEditar && (
              <span style={{ fontSize: 11, color: 'var(--meta)', marginRight: 'auto' }}>
                Só {primeiroNome(membro.nome)} (ou um admin) pode editar estas horas.
              </span>
            )}
            {podeEditar && (
              <button
                className="clr"
                onClick={() => {
                  if (confirm(`Limpar as horas de ${nomeMes(anoMes)} para ${membro.nome}?`)) {
                    limpar.mutate();
                  }
                }}
              >
                Limpar mês
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * O campo "em que trabalhou": um textarea que cresce com o texto.
 *
 * <p>Era um input de linha única — texto mais largo que a caixa fugia para o canto e
 * só se lia arrastando o cursor. Aqui a altura acompanha o conteúdo (scrollHeight a
 * cada mudança), então descrever o dia inteiro quebra em linhas visíveis.
 */
function CampoQueCresce({
  valor,
  placeholder,
  desabilitado,
  aoMudar,
}: {
  valor: string;
  placeholder: string;
  desabilitado: boolean;
  aoMudar(texto: string): void;
}) {
  const campo = useRef<HTMLTextAreaElement>(null);

  // Ajusta na montagem e sempre que o valor mudar por fora (troca de mês, limpar).
  useEffect(() => {
    const el = campo.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [valor]);

  return (
    <textarea
      ref={campo}
      rows={1}
      value={valor}
      placeholder={placeholder}
      disabled={desabilitado}
      onChange={(e) => aoMudar(e.target.value)}
    />
  );
}

/**
 * O gráfico do modal: uma barra por dia do mês.
 *
 * <p>Com 31 barras não cabe o número em cima de cada uma nem o rótulo de todas —
 * escrever tudo vira uma faixa cinza ilegível. Então só o dia de pico ganha o valor, e
 * o eixo marca de 5 em 5, mais o dia de hoje. O fim de semana fica mais apagado: é a
 * pista que explica por que a barra caiu ali.
 */
function GraficoBarras({ valores, dias, cor }: { valores: number[]; dias: Dia[]; cor: string }) {
  /*
   * 560×180 → 1000×230, e o desenho deixou de ser esticado.
   *
   * O SVG usava `preserveAspectRatio="none"` com altura fixa no CSS: a caixa mandava e o
   * desenho se deformava para caber. Isso passa despercebido enquanto a caixa tem quase
   * a proporção do viewBox — e o modal, ao ficar bem mais largo, deixou de ter: as barras
   * ficariam gordas e os números do eixo, achatados na horizontal.
   *
   * O viewBox agora tem a proporção que o CSS reserva (`aspect-ratio`), então o desenho
   * escala inteiro, sem deformar texto. E é largo e baixo de propósito: o gráfico ocupa a
   * largura do modal, e 31 barras num quadrado alto empurrariam a tabela para fora da
   * tela — o gráfico é o resumo, a tabela é o trabalho.
   */
  const W = 1000;
  const H = 230;
  const L = 34;
  const R = 10;
  const T = 18;
  const B = 30;
  const iw = W - L - R;
  const ih = H - T - B;
  const teto = Math.max(4, Math.ceil(Math.max(...valores, 0) / 4) * 4);
  const passo = iw / Math.max(1, valores.length);
  const largura = Math.max(3, passo * 0.62);
  const pico = valores.reduce((melhor, v, i) => (v > valores[melhor] ? i : melhor), 0);

  return (
    <svg className="grafico-membro" viewBox={`0 0 ${W} ${H}`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const y = T + ih - (ih * i) / 4;
        return (
          <g key={i}>
            <line className="gl" x1={L} y1={y} x2={W - R} y2={y} />
            <text className="yl" x={L - 7} y={y + 3}>
              {Math.round((teto * i) / 4)}
            </text>
          </g>
        );
      })}
      {valores.map((v, i) => {
        const dia = dias[i];
        const altura = teto ? (v / teto) * ih : 0;
        const cx = L + passo * i + passo / 2;
        const y = T + ih - altura;
        const rotula = dia && (dia.dia === 1 || dia.dia % 5 === 0 || dia.hoje);
        return (
          <g key={dia?.data ?? i}>
            <rect
              x={cx - largura / 2}
              y={y}
              width={largura}
              height={Math.max(0, altura)}
              rx={2}
              fill={cor}
              opacity={dia && fimDeSemana(dia) ? 0.45 : 0.9}
            />
            {i === pico && v > 0 && (
              <text className="vl" x={cx} y={y - 5}>
                {fmtH(v)}
              </text>
            )}
            {rotula && (
              <text
                className="xl"
                x={cx}
                y={H - 10}
                style={dia.hoje ? { fill: 'var(--gold)', fontWeight: 700 } : undefined}
              >
                {dia.dia}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/** Comparativo do time: uma linha por membro — port de graficoTime(). */
function ComparativoDoTime({
  grade,
  ocultos,
  foco,
  alternar,
  focar,
}: {
  grade: GradeHoras;
  ocultos: Set<number>;
  foco: number | null;
  alternar(id: number): void;
  focar(id: number | null): void;
}) {
  const dados = useMemo(
    () =>
      grade.membros.map((m) => ({
        ...m,
        horas: m.celulas.map((c) => Number(c.horas)),
        total: totalDe(m),
      })),
    [grade]
  );

  const visiveis = dados.filter((d) => !ocultos.has(d.id));
  const pico = Math.max(0, ...visiveis.flatMap((d) => d.horas));

  /*
   * Do dia 1 até hoje, zeros inclusive — a regra e o porquê estão em
   * `serieDoComparativo.ts`.
   */
  const limite = limiteDeHoje(grade.dias);
  const serieDe = (horas: number[]) => serieAteHoje(horas, limite);
  const passoEscala = pico <= 8 ? 2 : pico <= 20 ? 5 : pico <= 50 ? 10 : 20;
  const teto = Math.max(passoEscala * 2, Math.ceil(pico / passoEscala) * passoEscala);
  const nLinhas = Math.min(8, teto / passoEscala);

  const W = 900;
  const H = 370;
  const L = 52;
  const R = 22;
  const T = 24;
  const B = 54;
  const iw = W - L - R;
  const ih = H - T - B;
  const n = grade.dias.length;
  const px = (i: number) => (n > 1 ? L + (iw * i) / (n - 1) : L + iw / 2);
  const py = (v: number) => T + ih - (v / teto) * ih;

  /**
   * O desvio que separa quem empatou no mesmo dia.
   *
   * <p>Duas pessoas com 3h no dia 2 desenham exatamente em cima uma da outra, e a que
   * for pintada depois engole a outra — some a linha e some a bolinha, sem nada na tela
   * dizendo que há duas ali.
   *
   * <p>O desvio vale para os três: o <b>ponto de partida</b>, o <b>caminho</b> e o
   * <b>ponto de chegada</b>. Tudo usa {@link pyd}, então a bolinha nunca sai de cima da
   * sua própria linha.
   *
   * <p><b>O passo é 6, e não 1 ou 2.</b> Comecei com 2 — um pixel para cada lado, o
   * pedido literal — e no navegador nada mudou: cada linha leva um halo de 4px, que
   * existe para separá-la do fundo, e a bolinha tem 8px de diâmetro. A 2px de distância
   * o halo da segunda cobre a primeira inteira. Seis é o que põe as duas <b>encostadas
   * e inteiras</b>: a pegada de uma linha com halo é 5px, e a bolinha encolhe para 6px
   * no empate (ver {@code raioDe}), de modo que duas se tangenciam em vez de se comer.
   *
   * <p>É pouco: na escala atual, 3px valem uns cinco minutos de trabalho. Ninguém lê um
   * gráfico de horas com essa precisão, e fora do chão o desvio é <b>simétrico ao
   * valor</b> — nenhuma série fica sistematicamente acima da outra, então a comparação de
   * quem trabalhou mais continua valendo.
   *
   * <p><b>No zero ele sobe o grupo inteiro</b>, porque o zero é a borda de baixo e metade
   * de um desvio simétrico cairia fora da área, grampeada no mesmo pixel. Ver
   * {@link desvioDoEmpate}.
   *
   * <p>O empate é apurado entre os <b>visíveis</b>: esconder alguém pela legenda desfaz
   * o empate dele e as linhas que restam voltam ao lugar exato.
   */
  const PASSO = 6;

  /**
   * O passo do empate <b>no zero</b>, que é mais apertado.
   *
   * <p>Seis serve para o empate no meio do gráfico, onde as linhas trazem bolinha: a de
   * 8px é o que obriga a distância. <b>No zero não há bolinha</b> — só dias com hora
   * ganham ponto —, então o que precisa caber é a linha e o seu halo de 4px, e três
   * bastam para os traços de 1px não virarem um só.
   *
   * <p>A diferença aparece porque o zero é onde mais gente empata: cinco pessoas sem
   * lançar no mesmo dia abriam um leque de 24px acima do eixo, e 24px nesta escala
   * passam de meia hora — perto o bastante de um valor real para o olho duvidar se
   * aquilo é zero mesmo. A doze fica claramente rente ao chão, e ainda dá para contar
   * quantas linhas são.
   */
  const PASSO_NO_CHAO = 3;

  const empatados = useMemo(() => {
    // dia → valor → ids, **na ordem de cima para baixo** em que serão desenhados.
    const porDia = new Map<number, Map<number, { id: number; vizinhanca: number }[]>>();

    visiveis.forEach((d) => {
      const serie = serieDe(d.horas);
      serie.forEach((v, i) => {
        // De onde a linha vem e para onde vai. É isto que decide quem fica em cima:
        // quem chega do alto continua no alto, e as linhas não se cruzam à toa.
        const vizinhos = [serie[i - 1], serie[i + 1]].filter((x) => x != null);
        const vizinhanca = vizinhos.length
          ? vizinhos.reduce((a, b) => a + b, 0) / vizinhos.length
          : v; // ponto solto: nada a comparar, fica pelo próprio valor

        if (!porDia.has(i)) porDia.set(i, new Map());
        const porValor = porDia.get(i)!;
        porValor.set(v, [...(porValor.get(v) ?? []), { id: d.id, vizinhanca }]);
      });
    });

    // Mais horas em volta → mais para cima. Empate na vizinhança mantém a ordem de
    // desenho, que é estável entre renderizações.
    porDia.forEach((porValor) =>
      porValor.forEach((lista) => lista.sort((a, b) => b.vizinhanca - a.vizinhanca))
    );
    return porDia;
    // `visiveis` é derivado de grade + ocultos, e é o que muda o empate.
  }, [grade, ocultos]);

  function desvio(id: number, i: number, v: number): number {
    const iguais = empatados.get(i)?.get(v);
    if (!iguais) return 0;
    const posicao = iguais.findIndex((x) => x.id === id);
    // A lista já vem de cima para baixo. No zero o desvio sobe o grupo inteiro em vez de
    // se abrir para os dois lados, e com passo mais curto — ver `desvioDoEmpate`.
    const noChao = v === 0;
    return desvioDoEmpate(posicao, iguais.length, noChao, noChao ? PASSO_NO_CHAO : PASSO);
  }

  /** O y final: o valor, mais o desvio do empate, sem sair da área do gráfico. */
  function pyd(id: number, i: number, v: number): number {
    return Math.min(T + ih, Math.max(T, py(v) + desvio(id, i, v)));
  }

  /**
   * O raio da bolinha: menor quando ela divide o ponto com outra.
   *
   * <p>Com 4 de raio, duas bolinhas separadas por 6px ainda se sobrepõem em 2px — e a de
   * cima come um pedaço da de baixo. Com 3 elas se tangenciam: ficam coladas, e dá para
   * contar quantas são. Sozinha, a bolinha continua no tamanho de sempre.
   */
  function raioDe(i: number, v: number): number {
    return (empatados.get(i)?.get(v)?.length ?? 1) > 1 ? 3 : 4;
  }

  const totalDoTime = dados.reduce((soma, d) => soma + d.total, 0);

  return (
    <div className={`cmp${foco != null ? ' hi' : ''}`}>
      <div className="cmph">
        <div className="t">Comparativo do time</div>
        <div className="s">
          Horas por dia · {nomeMes(grade.anoMes)} · {fmtH(totalDoTime)} no total
        </div>
      </div>

      {totalDoTime > 0 ? (
        <svg className="cmpc" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
          {Array.from({ length: nLinhas + 1 }, (_, i) => {
            const y = T + ih - (ih * i) / nLinhas;
            return (
              <g key={i}>
                <line className={i === 0 ? 'gl0' : 'gl'} x1={L} y1={y} x2={W - R} y2={y} />
                <text className="yl" x={L - 10} y={y + 4}>
                  {Math.round((teto * i) / nLinhas)}h
                </text>
              </g>
            );
          })}
          {/*
            31 dias no eixo: a grade vertical fica só nas segundas-feiras, que é o que
            dá ritmo de semana sem virar uma cerca. O rótulo sai de 5 em 5 (mais o dia
            1 e o de hoje) porque 31 números lado a lado se sobrepõem e não se leem.
          */}
          {grade.dias.map((dia, i) => {
            const marca = dia.diaSemana === 1;
            const rotula = dia.dia === 1 || dia.dia % 5 === 0 || dia.hoje;
            return (
              <g key={dia.data}>
                {(marca || dia.hoje) && (
                  <line
                    className="gl"
                    x1={px(i)}
                    y1={T}
                    x2={px(i)}
                    y2={T + ih}
                    style={{ opacity: dia.hoje ? 0.9 : 0.35 }}
                  />
                )}
                {rotula && (
                  <text
                    className="xl"
                    x={px(i)}
                    y={H - 26}
                    style={dia.hoje ? { fill: 'var(--gold)', fontWeight: 700 } : undefined}
                  >
                    {dia.dia}
                  </text>
                )}
                {dia.hoje && (
                  <text className="xs" x={px(i)} y={H - 10} style={{ fill: 'var(--gold)' }}>
                    hoje
                  </text>
                )}
              </g>
            );
          })}
          {visiveis.map((d) => {
            const serie = serieDe(d.horas);
            if (serie.length === 0) return null;
            const pontos = serie
              .map((v, i) => `${px(i).toFixed(1)},${pyd(d.id, i, v).toFixed(1)}`)
              .join(' ');
            return (
              <g key={d.id} className={`serie${foco === d.id ? ' on' : ''}`}>
                <polyline className="halo" points={pontos} />
                <polyline className="ln" points={pontos} style={{ stroke: d.cor }} />
                {/*
                  Só os dias com hora ganham ponto, e nenhum ganha rótulo fixo: seis
                  pessoas × 31 dias seriam 186 bolinhas e 186 números empilhados. O
                  valor vem no title, ao passar o mouse.
                */}
                {serie.map((v, i) =>
                  v > 0 ? (
                    <circle
                      key={grade.dias[i].data}
                      className="pt"
                      cx={px(i)}
                      // Mesmo desvio da linha: a bolinha tem de ficar em cima dela.
                      cy={pyd(d.id, i, v)}
                      r={raioDe(i, v)}
                      style={{ fill: d.cor }}
                    >
                      <title>
                        {d.nome} · dia {grade.dias[i].dia}: {fmtH(v)}
                      </title>
                    </circle>
                  ) : null
                )}
              </g>
            );
          })}
        </svg>
      ) : (
        <div className="cmpvazio">
          Sem horas lançadas em {nomeMes(grade.anoMes)}. Clique num membro acima para lançar.
        </div>
      )}

      <div className="cmpleg">
        {dados.map((d) => (
          <div
            key={d.id}
            className={`lgi${ocultos.has(d.id) ? ' off' : ''}`}
            style={{ '--c': d.cor } as React.CSSProperties}
            onClick={() => alternar(d.id)}
            onMouseEnter={() => !ocultos.has(d.id) && focar(d.id)}
            onMouseLeave={() => focar(null)}
          >
            <span className="sw" />
            <b>{primeiroNome(d.nome)}</b>
            <span>{fmtH(d.total)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

